import {Progress as ChakraProgress} from "@chakra-ui/react";
import {InfoTip} from "./toggle-tip";
import * as React from "react";

export const ProgressBar = React.forwardRef<HTMLDivElement, ChakraProgress.TrackProps>(
  function ProgressBar(props, ref) {
    return (
      <ChakraProgress.Track {...props} ref={ref}>
        <ChakraProgress.Range />
      </ChakraProgress.Track>
    );
  },
);

export interface ProgressLabelProps extends ChakraProgress.LabelProps {
  info?: React.ReactNode;
}

export const ProgressLabel = React.forwardRef<HTMLDivElement, ProgressLabelProps>(function ProgressLabel(props, ref) {
  const {children, info, ...rest} = props;
  return (
    <ChakraProgress.Label {...rest} ref={ref}>
      {children}
      {info && <InfoTip>{info}</InfoTip>}
    </ChakraProgress.Label>
  );
});

export const ProgressRoot = ChakraProgress.Root;
export const ProgressValueText = ChakraProgress.ValueText;

export interface MultiProgressSegment {
  percentage?: number;
  color?: string;
  label?: string;
}

export interface MultiProgressProps extends Omit<ChakraProgress.TrackProps, "children"> {
  segments: MultiProgressSegment[];
  total?: number;
  showLabels?: boolean;
}

export const MultiProgressBar = React.forwardRef<HTMLDivElement, MultiProgressProps>(
  function MultiProgressBar(props, ref) {
    const {segments, showLabels = false, ...trackProps} = props;

    const colors = ["blue.500", "green.500", "orange.500", "purple.500", "pink.500", "teal.500", "red.500", "cyan.500"];

    const totalPercentage = segments.reduce((sum, segment) => sum + (segment.percentage || 0), 0);
    let currentPosition = 0;

    return (
      <div>
        <ChakraProgress.Root value={totalPercentage} max={100}>
          <ChakraProgress.Track {...trackProps} ref={ref} position="relative" overflow="hidden">
            {segments.map((segment, index) => {
              const percentage = segment.percentage || 0;
              const segmentColor = segment.color || colors[index % colors.length];
              const left = currentPosition;
              currentPosition += percentage;

              return (
                <div
                  key={index}
                  style={{
                    position: "absolute",
                    left: `${left}%`,
                    width: `${percentage}%`,
                    height: "100%",
                    backgroundColor: `var(--chakra-colors-${segmentColor.replace(".", "-")})`,
                    transition: "all 0.3s ease",
                  }}
                />
              );
            })}
          </ChakraProgress.Track>
        </ChakraProgress.Root>

        {showLabels && (
          <div style={{marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "12px"}}>
            {segments.map((segment, index) => {
              if (!segment.label) return null;

              const percentage = segment.percentage || 0;
              const segmentColor = segment.color || colors[index % colors.length];

              return (
                <div key={index} style={{display: "flex", alignItems: "center", gap: "6px"}}>
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: `var(--chakra-colors-${segmentColor.replace(".", "-")})`,
                    }}
                  />
                  <span style={{fontSize: "14px"}}>
                    {segment.label}: {percentage.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
